import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAlert } from "./useAlert";
import { IPartner } from "./usePartners";

export interface ILottery {
  _id: string;
  partner: IPartner | string;
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  image: string;
  thumbnail: string;
  en_prizeTitle: string;
  nl_prizeTitle: string;
  en_prizeDescription: string;
  nl_prizeDescription: string;
  drawDate: Date;
  /** When the lottery becomes visible in the app. Null/absent = live now. */
  startsAt?: Date | string | null;
  status: "PENDING" | "ACTIVE" | "COMPLETED";
  winners: string[];
  numberOfWinners?: number;
  isRecurring?: boolean;
  recurringInterval?: "DAILY" | "WEEKLY" | "MONTHLY";
  acceptingMaterials?: string[];
  includeStatiegeldDrops?: boolean;
  cities?: string[];
  locationType?: "CITY" | "LOCATION";
  locationIds?: string[];
  allowMultipleEntries?: boolean;
  numberOfParticipants?: number;
  numberOfTickets?: number;
  numberOfVouchersAvailable?: number;
  locationNames?: string[];
  updatedAt: Date;
  createdAt: Date;
}

export interface ILotteryUser {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  ticketCount?: number;
}

export interface ILotteryUsersPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ILotteryUsersResponse {
  users: ILotteryUser[];
  pagination: ILotteryUsersPagination;
}

interface IStore {
  /** State */
  allLotteries: ILottery[];
  oneLottery: ILottery | null;
  addedLottery: any | null;
  updatedLottery: any | null;
  deletedLottery: any | null;
  lotteryUsers: ILotteryUser[];
  lotteryUsersPagination: ILotteryUsersPagination | null;
  hasNextPage: boolean;
  /** Callbacks */
  getAllLotteries: (
    page: number,
    limit: number,
    search?: string,
    partnerId?: string,
    activeOnly?: boolean
  ) => void;
  getOneLottery: (id: string) => void;
  addLottery: (lottery: ILottery) => void;
  updateLottery: (id: string, lottery: ILottery) => void;
  deleteLottery: (id: string) => void;
  getLotteryUsers: (
    id: string,
    page?: number,
    limit?: number,
    append?: boolean
  ) => void;
  clearAddedLottery: () => void;
  clearUpdatedLottery: () => void;
  clearDeletedLottery: () => void;
  clearLotteryUsers: () => void;
}

export const useLottery = create<IStore>()((set) => ({
  /** State */
  allLotteries: [],
  oneLottery: null,
  addedLottery: null,
  updatedLottery: null,
  deletedLottery: null,
  lotteryUsers: [],
  lotteryUsersPagination: null,
  hasNextPage: false,
  /** Callbacks */
  getAllLotteries: async (page, limit, search, partnerId, activeOnly) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const searchParam =
        typeof search === "string" && search.trim()
          ? `&search=${encodeURIComponent(search.trim())}`
          : "";
      const partnerParam =
        typeof partnerId === "string" && partnerId.trim()
          ? `&partnerId=${encodeURIComponent(partnerId.trim())}`
          : "";
      const activeParam = activeOnly ? `&activeOnly=true` : "";
      const response = await axios.get(
        `lottery?page=${page}&limit=${limit}&dashboard=true${searchParam}${partnerParam}${activeParam}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          allLotteries:
            page === 1
              ? response?.data
              : [...state.allLotteries, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching all Lotteries", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneLottery: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`lottery/${id}`);
      if (response?.data) {
        set({ oneLottery: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Lottery", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addLottery: async (lottery) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("lottery", lottery);
      if (response?.data) {
        set({ addedLottery: response.data?._id });
      }
    } catch (error) {
      // @ts-ignore
      if (error?.data?.message) {
        useAlert.getState().setAlert({
          isOpen: true,
          // @ts-ignore
          message: error?.data?.message,
          severity: "error",
        });
      }
      LOG.error("Error on adding a Lottery", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateLottery: async (id, lottery) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`lottery/${id}`, lottery);
      if (response?.data) {
        set({ updatedLottery: response.data });
      }
    } catch (error) {
      LOG.error("Error on updating a Lottery", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteLottery: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`lottery/${id}`);
      if (response?.data) {
        set((state) => ({
          deletedLottery: response.data,
          allLotteries: state.allLotteries.filter(
            (lottery) => lottery._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting a Lottery", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedLottery: () => {
    set({ addedLottery: null });
  },
  clearUpdatedLottery: () => {
    set({ updatedLottery: null });
  },
  clearDeletedLottery: () => {
    set({ deletedLottery: null });
  },
  getLotteryUsers: async (id: string, page = 1, limit = 10, append = false) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `lottery/${id}/users?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          lotteryUsers: append
            ? [...state.lotteryUsers, ...response.data.users]
            : response.data.users,
          lotteryUsersPagination: response.data.pagination,
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching lottery users", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearLotteryUsers: () => {
    set({ lotteryUsers: [], lotteryUsersPagination: null });
  },
}));
